import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { BallMover, TaskType } from '../../../../../main/types/asset/asset.enums.ts';

import {
  BALL_ANIMATION_DEFAULT_DURATION_MS,
  BALL_ANIMATION_DEFAULT_TO_X_POSITION,
} from '../../../../constants/ball.ts';
import { StepGraderType } from '../../../../constants/grading.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../../../constants/speech-recognition.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible, setInteractiveMode, setupBallAnimation } from '../../../../slices/ball/ball.slice.ts';
import { BasePronunciationGraderInterface } from '../../../../types/pronunciation-grader.types.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Base abstract handler for user pronunciation steps (UserPronounce, UserPronounceFast, UserSayLetterName)
 * Contains shared logic for pronunciation steps while allowing specific handlers to customize grader and reference text
 */
export abstract class UserSayBaseStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  protected lastRecognizedText: string | null = null;
  protected grader: BasePronunciationGraderInterface | null = null;

  // Abstract methods to be implemented by specific handlers
  protected abstract getHandlerName(): string;
  protected abstract getGraderType(): StepGraderType;

  protected async determineNextState(): Promise<AppStateEnum | null> {
    const shouldStartRecording = await this.isOneOfTasks([TaskType.SoundsWriting]);
    return shouldStartRecording ? AppStateEnum.Recording : null;
  }

  protected override async onRecording(): Promise<void> {
    console.log(`${this.getHandlerName()} onRecording`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Check if ball should be draggable
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);

    if (step.ballMover === BallMover.User) {
      // Enable interactive mode (sets draggable and visible)
      dispatch(setInteractiveMode(true));
    } else if (await this.shouldAnimateBall()) {
      // Set animation with visibility and draggability in a single action
      dispatch(
        setupBallAnimation({
          sequence: [
            {
              toXPosition: BALL_ANIMATION_DEFAULT_TO_X_POSITION,
              duration: BALL_ANIMATION_DEFAULT_DURATION_MS,
            },
          ],
        })
      );
    } else {
      // Just make the ball visible
      dispatch(setBallIsVisible(true));
    }

    try {
      if (!this.grader) {
        throw new Error('Grader not initialized');
      }

      const result = await this.grader.startGrading();
      console.log(`${this.getHandlerName()} onRecording result`, result);
      const isCorrect = result ? result.isCorrect : false;
      this.lastRecognizedText = result?.details?.recognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE;
      await dispatch(
        updateAppStateThunk({ state: isCorrect ? AppStateEnum.Correct : AppStateEnum.Incorrect })
      ).unwrap();
    } catch (error) {
      this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    await this.handleStartStep();
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    if (this.grader) {
      await this.grader.abort();
    }
    await super.handleCorrect({
      assuming: false,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    if (this.grader) {
      await this.grader.abort();
    }
    await super.handleCorrect({
      assuming: true,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await super.handleIncorrect(this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onAborting(): Promise<void> {
    console.log(`${this.getHandlerName()} onAborting`);

    // Set ball visibility to false as in original implementation
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(false));

    this.grader?.abort();
    await super.onAborting();
  }

  protected override async onReadyForNextStep(): Promise<void> {
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    super.onReadyForNextStep();
    await this.handleReadyForNextStep();
  }

  protected async initializeGrader(): Promise<void> {
    try {
      const state = await this.reduxStoreUtil.getRootState();
      const { lesson, task, step } = selectCurrentLessonTaskStep(state);
      if (lesson && task && step) {
        this.grader = await this.pronunciationGraderFactory.createGrader({
          type: this.getGraderType(),
        });
      }
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  protected async handleStartStep(): Promise<void> {
    this.lastRecognizedText = null;

    const dispatch = await this.reduxStoreUtil.getDispatch();

    const graderPromise = this.initializeGrader();

    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    await graderPromise;

    // Determine next state based on handler-specific logic
    const nextState = await this.determineNextState();
    if (nextState) {
      await dispatch(updateAppStateThunk({ state: nextState, blockAnimation: true })).unwrap();
      return;
    }
    dispatch(setBallIsVisible(true));
  }
}
